
import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { storiesData } from "@/data/stories";
import { sdgsData } from "@/data/sdgs";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { ArrowLeft } from "lucide-react";
import { getImagePreviewPath } from "@/components/admin/stories/utils/imageHandlers";

const StoryPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  
  const story = storiesData.find(s => s.slug === slug);
  
  // Handle if story is not found
  if (!story) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">{language === "en" ? "Story not found" : "لم يتم العثور على القصة"}</h1>
          <Link to="/" className="text-palsustain-green hover:underline">
            {language === "en" ? "Back to Home" : "العودة إلى الصفحة الرئيسية"}
          </Link>
        </div>
      </div>
    );
  }
  
  const relatedSDGs = sdgsData.filter(sdg => story.sdgIds.includes(sdg.id));
  
  // Get appropriate image for story using our utility function
  const imagePath = getImagePreviewPath(story.image);
  
  // Enhanced image error handling
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.log("Story page image failed to load:", e.currentTarget.src);
    
    // Try a story-specific fallback based on ID
    if (story.id) {
      let fallbackPath;
      switch (story.id) {
        case 1: 
          fallbackPath = "/lovable-uploads/d2cfe4d7-2bad-4598-aced-11e4ac3e36d2.png"; // Solar panel image
          break;
        case 2:
          fallbackPath = "/lovable-uploads/5345efbc-ab4d-4769-a9ac-d2d94af7d692.png"; // Farming image
          break;
        case 3:
          fallbackPath = "/lovable-uploads/7e3bb041-335c-45a2-a9ec-92bbc276ca44.png"; // Updated startup image
          break;
        case 4:
          fallbackPath = "/lovable-uploads/d6a911f7-e55a-4331-a528-f6d5dcc6ae41.png"; // Water image
          break;
        case 5:
          fallbackPath = "/lovable-uploads/1b9e0703-9313-4b25-a35e-76332d449bbe.png"; // Women image
          break;
        default:
          fallbackPath = "/lovable-uploads/0bed4794-3e56-4a50-9f99-773ee667b61c.png"; // Default image
      }
      console.log(`Using fallback image for story ID ${story.id}:`, fallbackPath);
      e.currentTarget.src = fallbackPath;
      return;
    }
    
    // Default fallback
    e.currentTarget.src = "/placeholder.svg";
    e.currentTarget.classList.add("bg-gray-100");
  };
  
  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    // Debug story image path
    console.log("StoryPage loaded with story:", {
      id: story.id,
      title: story.title.en,
      originalImagePath: story.image,
      resolvedImagePath: imagePath
    });
  }, [slug, imagePath, story]);

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <article className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Back button */}
            <Link 
              to="/" 
              className={`inline-flex items-center mb-8 text-palsustain-charcoal/70 hover:text-palsustain-green transition-colors ${
                language === "ar" ? "flex-row-reverse" : ""
              }`}
            >
              <ArrowLeft className={`h-4 w-4 ${language === "ar" ? "ml-2 transform rotate-180" : "mr-2"}`} />
              {language === "en" ? "Back to stories" : "العودة إلى القصص"}
            </Link>
            
            {/* Story header */}
            <header className="mb-10">
              <div className="mb-4 flex flex-wrap gap-2">
                {relatedSDGs.map((sdg) => (
                  <Link 
                    key={sdg.id}
                    to={`/sdgs/${sdg.slug}`}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium" 
                    style={{ backgroundColor: sdg.color, color: "white" }}
                  >
                    {language === "en" ? `SDG ${sdg.id}: ${sdg.shortTitle.en}` : `الهدف ${sdg.id}: ${sdg.shortTitle.ar}`}
                  </Link>
                ))}
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-palsustain-charcoal mb-4">
                {language === "en" ? story.title.en : story.title.ar}
              </h1>
              
              {/* Removed author and date section */}
            </header>
            
            {/* Featured image */}
            <div className="mb-10 rounded-xl overflow-hidden shadow-md">
              <img 
                src={imagePath}
                alt={language === "en" ? story.title.en : story.title.ar}
                className="w-full h-auto object-cover"
                onError={handleImageError}
                data-story-id={story.id}
              />
            </div>
            
            {/* Story content */}
            <div className="prose prose-lg max-w-none">
              {(language === "en" ? story.content.en : story.content.ar)
                .split("\n\n")
                .map((paragraph, index) => (
                  <p key={index} className="mb-6 text-palsustain-charcoal/90 leading-relaxed">
                    {paragraph}
                  </p>
                ))
              }
            </div>
            
            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-palsustain-beige/50">
              <div className="flex flex-wrap gap-2">
                {story.tags.map((tag) => (
                  <span 
                    key={tag}
                    className="px-3 py-1 bg-palsustain-beige/30 text-palsustain-charcoal/80 rounded-full text-sm"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default StoryPage;
